import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { standardAndRecipeBpmServices } from 'services/ReferenceScreenServices/standardAndRecipeBpmServices';
import {
  // StandardAndRecipeBpm,
  // StandardAndRecipeBpmResponse,
  // StandardAndRecipeBpmSingleResponse,
  // StandardAndRecipeBpmPayload,
  BpmByNDCAndJobType,
  BpmByNDCAndJobTypePayload
} from 'Interfaces/ReferenceInterfaces/StandardAndRecipeBpmInterface';

export class StandardAndRecipeBpmStore {
  standardAndRecipeBpms: BpmByNDCAndJobType[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedStandardAndRecipeBpm: BpmByNDCAndJobType | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchStandardAndRecipeBpms = async (page = 1, pageSize = 100) => {
    this.isLoading = true;
    try {
      const response = await standardAndRecipeBpmServices.getAllStandardAndRecipeBpms(
        page,
        pageSize
      );
      runInAction(() => {
        this.standardAndRecipeBpms = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching standard and recipe BPMs');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollStandardAndRecipeBpms = async (page = 1, pageSize = 100) => {
    this.isPolling = true;
    try {
      const response = await standardAndRecipeBpmServices.getAllStandardAndRecipeBpms(
        page,
        pageSize
      );
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.standardAndRecipeBpms) !== JSON.stringify(response.data)) {
          this.standardAndRecipeBpms = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if(this.environment === 'development') {
        console.error('Error polling standard and recipe BPMs:', error);
      }
      toast.error('Error polling standard and recipe BPMs!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 100) => {
    await this.fetchStandardAndRecipeBpms(page, pageSize);
  };

  createStandardAndRecipeBpm = async (
    standardAndRecipeBpmData: BpmByNDCAndJobTypePayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response =
        await standardAndRecipeBpmServices.createStandardAndRecipeBpm(standardAndRecipeBpmData);
      if (response.status) {
        toast.success('Standard and recipe BPM created successfully');
        await this.fetchStandardAndRecipeBpms(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create standard and recipe BPM');
        return false;
      }
    } catch (error) {
      toast.error('Error creating standard and recipe BPM');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateStandardAndRecipeBpm = async (
    id: number,
    standardAndRecipeBpmData: BpmByNDCAndJobTypePayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await standardAndRecipeBpmServices.updateStandardAndRecipeBpm(
        id,
        standardAndRecipeBpmData
      );
      if (response.status) {
        toast.success('Standard and recipe BPM updated successfully');
        await this.fetchStandardAndRecipeBpms(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update standard and recipe BPM');
        return false;
      }
    } catch (error) {
      toast.error('Error updating standard and recipe BPM');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteStandardAndRecipeBpm = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await standardAndRecipeBpmServices.deleteStandardAndRecipeBpm(id);
      if (response.status) {
        toast.success('Standard and recipe BPM deleted successfully');

        const isLastItemOnPage = this.standardAndRecipeBpms.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchStandardAndRecipeBpms(this.pagination.currentPage - 1);
        } else {
          await this.fetchStandardAndRecipeBpms(this.pagination.currentPage);
        }
      } else {
        toast.error(response.message || 'Failed to delete standard and recipe BPM');
      }
    } catch (error) {
      toast.error('Error deleting standard and recipe BPM');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedStandardAndRecipeBpm = (standardAndRecipeBpm: BpmByNDCAndJobType | null) => {
    this.selectedStandardAndRecipeBpm = standardAndRecipeBpm;
  };
}

export const standardAndRecipeBpmStore = new StandardAndRecipeBpmStore();
